import { useState, Suspense, lazy, useMemo } from 'react';
import { Users2, BarChart3, Clock, ClipboardList, Calendar, Network } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { HubShell, type HubTab } from '@/components/common/HubShell';
import { usePermissions } from '@/contexts/PermissionContext';
import { LEGACY_TAB_TO_SECTION, SHIFT_SECTIONS, SHIFT_SECTION_PARAM } from '@/pages/hr/shift-sections';

const HRDashboard = lazy(() => import('@/pages/hr/HRDashboard'));
const HRAttendance = lazy(() => import('@/pages/hr/HRAttendance'));
const HRRequests = lazy(() => import('@/pages/hr/HRRequests'));
const ShiftHub = lazy(() => import('@/pages/hr/ShiftHub'));

const HROrgChart = lazy(() => import('@/components/hr/org-chart/OrgChartPage'));
type HrTabId = 'dashboard' | 'attendance' | 'requests' | 'shifts' | 'organigramma';

interface HrTab extends HubTab<HrTabId> {
  /** Visibile se l'utente ha ALMENO uno di questi permessi. */
  permissions: ReadonlyArray<string>;
}

// Le sezioni HR sono guidate dai permessi (Task #2559): ogni tab è visibile solo
// se il ruolo possiede il permesso corrispondente. admin/super_admin passano per
// lo short-circuit di PermissionContext.
// Shell condivisa con CRM/Magazzino (HubShell): stessa grandezza di titolo e tab.
// "Template Turni", "QR Sedi" e "Impostazioni" sono sotto-sezioni di Pianificazione Turni
// (ShiftHub): la tab è visibile se l'utente può vedere almeno una di esse.
const HR_TABS: HrTab[] = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3, permissions: ['hr.dashboard.view'] },
  { key: 'attendance', label: 'Presenze', icon: Clock, permissions: ['hr.attendance.view'] },
  { key: 'requests', label: 'Richieste', icon: ClipboardList, permissions: ['hr.requests.view'] },
  { key: 'shifts', label: 'Pianificazione Turni', mobileLabel: 'Turni', icon: Calendar, permissions: SHIFT_SECTIONS.map((s) => s.permission) },
  { key: 'organigramma', label: 'Teams & Settori', mobileLabel: 'Teams', icon: Network, permissions: ['hr.settings.manage'] },
];

const Loader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
  </div>
);

export default function HRPage() {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = useMemo(() => HR_TABS.filter(tab => tab.permissions.some((p) => hasPermission(p))), [hasPermission]);
  const [activeTabId, setActiveTabId] = useState<HrTabId | null>(null);

  const rawRequestedTab = searchParams.get('tab');
  // Deep-link legacy ?tab=templates|qr-stations|settings → tab Pianificazione Turni (ShiftHub legge la sotto-sezione).
  // ?section=… senza ?tab (ShiftHub lo scrive) → resta su Pianificazione Turni.
  const requestedTab = rawRequestedTab
    ? (rawRequestedTab in LEGACY_TAB_TO_SECTION ? 'shifts' : rawRequestedTab)
    : (searchParams.has(SHIFT_SECTION_PARAM) ? 'shifts' : null);
  const activeTab = tabs.find(t => t.key === (requestedTab ?? activeTabId)) ?? tabs[0] ?? null;

  const selectTab = (id: HrTabId) => {
    setActiveTabId(id);
    if (!requestedTab && !searchParams.has(SHIFT_SECTION_PARAM)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tab');
      next.delete('store');
      next.delete(SHIFT_SECTION_PARAM);
      return next;
    });
  };

  const tabId = activeTab?.key ?? null;

  return (
    <HubShell<HrTabId>
      icon={Users2}
      title="Gestione HR"
      subtitle="presenze, richieste, turni e squadre"
      tabs={tabs}
      activeTab={tabId ?? 'dashboard'}
      onTabChange={selectTab}
      mainClassName="flex-1 overflow-auto"
    >
      <Suspense fallback={<Loader />}>
        {tabId === 'dashboard' && <HRDashboard />}
        {tabId === 'attendance' && <HRAttendance />}
        {tabId === 'requests' && <HRRequests />}
        {tabId === 'shifts' && <ShiftHub />}
        {tabId === 'organigramma' && <HROrgChart />}
        {!tabId && (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Non hai i permessi per accedere ad alcuna sezione HR.
          </div>
        )}
      </Suspense>
    </HubShell>
  );
}
